package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rest.unifiedaggregator.IncomingEvent
import unifiedcontent.aggregator.PartialSchema
import org.jetbrains.compose.web.css.*

private val ROOT_FIELD_PATTERN = Regex("""\$\.(\w+)""")
private val SYSTEM_FIELDS = setOf("id", "type", "version")

/**
 * Information about a partial that overrides a field.
 * Used to display partial override indicators in the Atlas view.
 */
data class PartialOverride(
    val partialId: String,
    val partialName: String
)

/**
 * Map of root field names to the partials that override them.
 */
typealias PartialOverrideMap = Map<String, List<PartialOverride>>

/**
 * Extract root field name from a JSON path.
 * E.g., "$.colors" -> "colors", "$.images.tile" -> "images"
 */
fun extractRootFieldFromPath(jsonPath: String): String? =
    ROOT_FIELD_PATTERN.find(jsonPath)?.groupValues?.get(1)

/**
 * Extract root field names from a partial's operations.
 * Prefers operations if available (more precise path targeting), falls back to data keys.
 */
fun extractOverriddenRootFields(event: IncomingEvent): Set<String> {
    val operationFields = event.operations?.mapNotNull { op ->
        extractRootFieldFromPath(op.targetPath)
    }?.toSet()

    if (!operationFields.isNullOrEmpty()) return operationFields

    // Operations not present — fall back to top-level data keys, excluding identity fields
    return event.data.keys.filterNot { it in SYSTEM_FIELDS }.toSet()
}

/**
 * Build a map of root fields to the partials that override them.
 */
fun buildPartialOverrideMap(
    partials: List<IncomingEvent>,
    partialLookup: (String) -> PartialSchema?
): PartialOverrideMap {
    val result = mutableMapOf<String, MutableList<PartialOverride>>()

    partials.forEach { event ->
        val partialId = event.partialId ?: return@forEach
        val partialName = partialLookup(partialId)?.name ?: partialId
        val rootFields = extractOverriddenRootFields(event)

        rootFields.forEach { field ->
            result.getOrPut(field) { mutableListOf() }
                .add(PartialOverride(partialId, partialName))
        }
    }

    return result
}

/**
 * Renders a single icon indicating a field is overridden by one or more partials.
 * Shows tooltip on hover listing all partial names.
 * For single partial: click navigates directly to Incoming tab.
 * For multiple partials: click shows menu to select which partial.
 */
@Composable
fun partialOverrideIndicator(
    overrides: List<PartialOverride>,
    onClick: ((String) -> Unit)?
) {
    val tooltipText = remember(overrides) {
        "Overridden by: ${overrides.joinToString(", ") { it.partialName }}"
    }

    button {
        frameless = true
        content {
            svgIcon(SVGIcons.stackForward) {
                size = 16.px
                title = tooltipText
                style {
                    marginLeft(4.px)
                    color(SXMUI.linkColor.value())
                    property("vertical-align", "middle")
                }
            }
        }
        if (overrides.size == 1) {
            action { onClick?.invoke(overrides.first().partialId) }
        } else {
            overrides.forEach { override ->
                subAction {
                    title = override.partialName
                    action { onClick?.invoke(override.partialId) }
                }
            }
        }
    }
}
